import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Field } from 'redux-form';
import { LoaderDots, Button } from '@reservamos/elements';
import { fieldRenderer } from 'utils/formRenderers';
import getPurchaseStepName from '../../../utils/purchase/getPurchaseStepName';
import 'styles/components/purchase/DiscountCode';
import 'styles/components/LoadingDots';
import './DiscountCode.scss';

const propTypes = {
  token: PropTypes.string.isRequired,
  selectedInstallmentsPlan: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  applyingDiscount: PropTypes.bool.isRequired,
  onApplyDiscount: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  features: PropTypes.object.isRequired,
  hasDynamicPricing: PropTypes.bool,
};

/**
 * DiscountCode component
 * Displays a form to apply discount codes during checkout
 * @param {object} props - token, selectedInstallmentsPlan, handleSubmit, applyingDiscount, onApplyDiscount, t, features, hasDynamicPricing.
 * @param {string} props.token - Authentication token for the purchase.
 * @param {object} props.selectedInstallmentsPlan - The selected payment installments plan.
 * @param {Function} props.handleSubmit - Redux-form submit handler.
 * @param {boolean} props.applyingDiscount - Whether a discount is currently being applied.
 * @param {Function} props.onApplyDiscount - Callback to apply a discount code.
 * @param {Function} props.t - Translation function from i18next.
 * @param {object} props.features - Feature flags from whitelabel configuration.
 * @param {boolean} props.hasDynamicPricing - Whether the route uses dynamic pricing. Coupon input is hidden only when this is true and the feature flag DISCOUNT_CODE_SKIP_ON_EXISTING_DISCOUNT is also enabled.
 */
class DiscountCode extends Component {
  constructor(props) {
    super(props);

    this.applyDiscount = this.applyDiscount.bind(this);
  }

  /**
   * Applies a discount code to the purchase
   * @param {Object} formValues - Form values containing the discount code
   * @param {string} formValues.discountCode - The discount code to apply
   */
  applyDiscount({ discountCode }) {
    const { token, selectedInstallmentsPlan, onApplyDiscount } = this.props;

    onApplyDiscount(token, discountCode, selectedInstallmentsPlan);
  }

  /**
   * Determines whether the discount code form should be visible.
   * @returns {boolean} True if the user is on checkout, DISCOUNT_CODE_ENABLED is on, and the combination of hasDynamicPricing with DISCOUNT_CODE_SKIP_ON_EXISTING_DISCOUNT does not suppress the input.
   */
  isDiscountCodeVisible() {
    const { features, hasDynamicPricing } = this.props;
    const purchaseStep = getPurchaseStepName(window.location.pathname);

    return (
      ['checkout', 'quickCheckout'].includes(purchaseStep) &&
      features.DISCOUNT_CODE_ENABLED &&
      !(hasDynamicPricing && features.DISCOUNT_CODE_SKIP_ON_EXISTING_DISCOUNT)
    );
  }

  render() {
    const { handleSubmit, applyingDiscount, t } = this.props;

    if (!this.isDiscountCodeVisible()) return null;

    return (
      <div className="discount-code">
        <p className="checkout-text">{t('text.discount_code')}</p>

        <form className="form-item-wrap inline-items" onSubmit={handleSubmit(this.applyDiscount)}>
          <div className="form-item-wrap-l">
            <Field
              type="text"
              name="discountCode"
              id="discountCodeSidebar"
              placeholder={t('payment:placeholder.discount_coupon')}
              placeholderOnly
              cls="form-item textfield"
              component={fieldRenderer}
            />
          </div>

          <div className="form-item-wrap-button">
            {applyingDiscount ? (
              <LoaderDots isAccent />
            ) : (
              <Button withHeight text={t('button.apply')} buttonType="submit" />
            )}
          </div>
        </form>
      </div>
    );
  }
}

DiscountCode.propTypes = propTypes;

const mapStateToProps = (state) => ({
  features: state.whitelabelConfig.features,
});

export default connect(mapStateToProps)(withTranslation('general')(DiscountCode));
